import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Header } from "../../Components/Index";
import { updateService } from "../../services/api/serviceApi";
import { extractErrorMessage } from "../../services/api/errorHandler";
import { buildDetailsWithNextServiceMileage, computeIntervalKm, saveNextServiceMileage } from "../../services/nextServiceMileage";
import { getVehicleReminders, updateReminderSettings } from "../../services/api/reminderApi";
import ReceiptIcon from "../ServiceThree/components/ReceiptIcon";
import VehiclePlate from "../ServiceOne/components/VehiclePlate";
import "../ServiceThree/ServiceThree.scss";
import "../ServiceOne/serviceOne.scss";
import "./serviceReview.scss";

const displayNumber = value => Number(value || 0).toLocaleString("fa-IR");

export default function ServiceReview() {
  const location = useLocation();
  const navigate = useNavigate();
  const draft = location.state?.serviceDraft;
  const isCustomerFlow = location.state?.customerServiceRegistration === true;
  const [cost, setCost] = useState("");
  const [showCostPopup, setShowCostPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [shareMessage, setShareMessage] = useState("");

  useEffect(() => {
    if (!draft) navigate(isCustomerFlow ? "/SelectService?view=details" : "/ServiceTwo", { replace: true });
  }, [draft, isCustomerFlow, navigate]);

  if (!draft) {
    return null;
  }

  const handleSubmit = async event => {
    event.preventDefault();
    if (loading) return;
    const costDigits = String(cost).replace(/[۰-۹]/g, digit => "۰۱۲۳۴۵۶۷۸۹".indexOf(digit)).replace(/[^\d]/g, "");
    const costValue = costDigits ? Number(costDigits) : null;
    if (!draft.serviceId) {
      setError("شناسه سرویس پیدا نشد.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const details = buildDetailsWithNextServiceMileage({}, draft.nextMileage);
      if (draft.mechanic) details.mechanicName = draft.mechanic;
      const response = await updateService(draft.serviceId, {
        vehicleId: draft.vehicleId,
        categoryId: draft.categoryId,
        mileage: draft.mileage,
        serviceDate: draft.serviceDate,
        cost: costValue,
        description: draft.description,
        details,
        type: draft.type,
      });
      if (!response?.success) throw new Error(response?.message || "ثبت سرویس انجام نشد");
      if (draft.nextMileage !== null) {
        saveNextServiceMileage({ vehicleId: draft.vehicleId, categoryId: draft.categoryId, nextServiceMileage: draft.nextMileage, serviceMileage: draft.mileage });
        const intervalKm = computeIntervalKm(draft.mileage, draft.nextMileage);
        if (intervalKm !== null) {
          const reminders = await getVehicleReminders(draft.vehicleId);
          const list = Array.isArray(reminders?.data) ? reminders.data : Array.isArray(reminders) ? reminders : [];
          const reminder = list.find(item => Number(item?.categoryId) === Number(draft.categoryId));
          if (reminder?.id) await updateReminderSettings(reminder.id, { intervalKm });
        }
      }
      setSaved(true);
    } catch (submitError) {
      setError(extractErrorMessage(submitError, "ثبت سرویس انجام نشد"));
    } finally {
      setLoading(false);
    }
  };

  const saveCost = event => {
    event.preventDefault();
    const digits = String(event.currentTarget.elements.cost.value)
      .replace(/[۰-۹]/g, digit => "۰۱۲۳۴۵۶۷۸۹".indexOf(digit))
      .replace(/[^\d]/g, "");
    if (!digits) return;
    setCost(Number(digits).toLocaleString("fa-IR"));
    setShowCostPopup(false);
    setError("");
  };

  const shareReview = async () => {
    const text = [
      "فاکتور سرویس خودرو",
      `نوع سرویس: ${draft.categoryTitle}`,
      `خودرو: ${vehicleName}`,
      `کیلومتر: ${displayNumber(draft.mileage)}`,
      `هزینه: ${cost || "ثبت نشده"} تومان`,
    ].join("\n");
    try {
      if (navigator.share) {
        await navigator.share({ title: "فاکتور سرویس خودرو", text });
        setShareMessage("اطلاعات سرویس برای اشتراک‌گذاری آماده شد");
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setShareMessage("اطلاعات سرویس کپی شد");
      }
    } catch (shareError) {
      if (shareError?.name !== "AbortError") setShareMessage("اشتراک‌گذاری انجام نشد");
    }
  };

  const vehicleName = draft.vehicle?.CarModel || draft.vehicle?.carModel || draft.vehicle?.title || draft.vehicle?.brand || "خودرو";
  const firstPlateValue = (...values) => values.find(value => value !== undefined && value !== null && String(value).trim() !== "") || "";
  const plateVehicle = {
    plate1: firstPlateValue(draft.vehicle?.plate1, draft.vehicle?.Plate1),
    plate3: firstPlateValue(draft.vehicle?.plate3, draft.vehicle?.Plate3),
    plate2: firstPlateValue(draft.vehicle?.plate2, draft.vehicle?.Plate2),
    plateEnd: firstPlateValue(draft.vehicle?.plateEnd, draft.vehicle?.PlateEnd, draft.vehicle?.End),
  };

  return <>
    <Header Text="فاکتور سرویس" hideR="hide" hideL="hide" showNotification={false} />
    <main className="service-receipt service-review" dir="rtl">
      <section className="receipt-hero service-review__hero">
        <span className="receipt-hero__check"><ReceiptIcon name="info" /></span>
        <div><small>سرویس ثبت شده است</small><h1>فاکتور سرویس</h1><p>اطلاعات فاکتور را بررسی کنید و موارد تکمیلی را وارد کنید.</p></div>
        <dl><div><dt>نوع سرویس</dt><dd>{draft.categoryTitle}</dd></div><div><dt>کیلومتر</dt><dd>{displayNumber(draft.mileage)}</dd></div></dl>
      </section>

      <section className="receipt-vehicle service-review__vehicle">
        <header><span><ReceiptIcon name="car" /></span><div><small>خودروی انتخاب‌شده</small><h2>{vehicleName}</h2></div><div className="service-review__plate"><VehiclePlate vehicle={plateVehicle} variant="card" /></div></header>
        <div className="receipt-vehicle__meta"><span><ReceiptIcon name="speed" /><small>کیلومتر بعدی</small><b>{draft.nextMileage === null ? "ثبت نشده" : displayNumber(draft.nextMileage)}</b></span><span><ReceiptIcon name="calendar" /><small>تاریخ سرویس</small><b>{draft.serviceDate || "—"}</b></span></div>
      </section>

      <section className="receipt-items service-review__operations">
        <header><div><ReceiptIcon name="receipt" /><h2>اقلام فاکتور</h2></div><span>۱ مورد</span></header>
        <div className="receipt-items__list"><button type="button" className="service-review__operation" onClick={() => setShowCostPopup(true)}><span>۱</span><div><h3>{draft.categoryTitle}</h3>{draft.mechanic && <p>مکانیک: {draft.mechanic}</p>}{draft.description && <p>{draft.description}</p>}</div><b>{cost ? `${cost} تومان` : "برای تکمیل هزینه لمس کنید"}</b></button></div>
      </section>

      <footer className="service-receipt__footer service-review__footer">
        {error && <p className="service-review__error" role="alert">{error}</p>}
        <p className="service-review__message" role="status">{shareMessage}</p>
        <button type="button" className="service-review__share" onClick={shareReview}><ReceiptIcon name="share" />اشتراک‌گذاری</button>
        <button type="button" className="service-review__submit" onClick={saved ? () => navigate("/", { replace: true }) : handleSubmit} disabled={loading}><ReceiptIcon name={saved ? "dashboard" : "check"} />{saved ? "بازگشت به داشبورد" : loading ? "در حال ذخیره..." : "ذخیره اطلاعات فاکتور"}</button>
      </footer>
    </main>
    {showCostPopup && <div className="service-review__overlay" role="presentation" onClick={() => setShowCostPopup(false)}>
      <form className="service-review__popup" role="dialog" aria-modal="true" aria-label="ثبت هزینه عملیات" onSubmit={saveCost} onClick={event => event.stopPropagation()}>
        <div className="service-review__popup-head"><div><b>تکمیل اطلاعات فاکتور</b><small>{draft.categoryTitle}</small></div><button type="button" aria-label="بستن" onClick={() => setShowCostPopup(false)}>×</button></div>
        <label><span>هزینه (تومان)</span><input name="cost" inputMode="numeric" autoFocus defaultValue={cost} placeholder="مبلغ را وارد کنید" /></label>
        <button type="submit" className="service-review__popup-save">ذخیره اطلاعات</button>
      </form>
    </div>}
  </>;
}
